/* eslint semi: 1 */
import { defineStore } from 'pinia';
import { TARGET_COMPONENTS } from '@/composables/file-upload/use-file-uploader';
import type { FileUploadItem } from '@base-shared/types/file-uploader';

type TargetComponentType = typeof TARGET_COMPONENTS[keyof typeof TARGET_COMPONENTS];

/**
 * Pinia store for managing file upload progress state.
 * Tracks which components are uploading files and displays a progress UI.
 *
 * State:
 * - isPopupVisible: Whether the upload progress popup is currently visible
 * - trackedComponents: Array of component types whose uploads are being tracked
 * - trackedUploadFiles: Array of files currently being uploaded
 */
export const useFileUploadProgressStore = defineStore('fileUploadProgressStore', {
  state: () => ({
    /** Controls visibility of the upload progress popup */
    isPopupVisible: true,
    /** Component types whose file uploads should be tracked and displayed */
    trackedComponents: [] as TargetComponentType[],
    /** Files currently being uploaded for the tracked components */
    trackedUploadFiles: [] as FileUploadItem[]
  }),
  actions: {
    /** Set the visibility state of the upload progress popup */
    setPopupVisible (isVisible: boolean) {
      this.isPopupVisible = isVisible;
    },

    /** Update which component types should be tracked for uploads */
    setTrackedComponents (components: TargetComponentType[]) {
      this.trackedComponents = components;
    },

    /** Update the list of files being tracked for upload progress */
    setTrackedUploadFiles (files: FileUploadItem[]) {
      this.trackedUploadFiles = files;
    },

    /** Reset all state to initial values */
    reset () {
      this.isPopupVisible = true;
      this.trackedComponents = [];
      this.trackedUploadFiles = [];
    }
  }
});
